import { useCallback, useEffect, useState } from 'react';

import {
  ActionIcon,
  Alert,
  Button,
  Center,
  Group,
  List,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure, useLocalStorage } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { jsonc } from '@shopify/lang-jsonc';
import { IconAlertCircle, IconDownload, IconEdit, IconHelp } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import CodeMirror from '@uiw/react-codemirror';
import { parse as parseJsonc } from 'jsonc-parser';
import { z } from 'zod';

import Chart3D from '@/components/Chart3D';
import { ConfigureChartButton } from '@/components/ConfigureChartButton';
import { CopyLinkButton } from '@/components/CopyLinkButton';
import { DownloadButton } from '@/components/DownloadButton';
import { RangeFilterButton } from '@/components/RangeFilterButton';
import { useChartTitle } from '@/contexts/ChartContext';
import { useChartDefaults } from '@/hooks/useChartDefaults';
import { useIsMobile } from '@/hooks/useIsMobile';
import { usePageActions } from '@/hooks/usePageActions';
import { decodeChartData, encodeChartData } from '@/lib/chartEncoding';
import { validateChartData } from '@/lib/dataValidation';
import type { ChartData } from '@/types/chart';

// Available example templates
const EXAMPLE_TEMPLATES = [
  { value: '/example-chart.jsonc', label: 'Simple Values (Preflop Equity)' },
  { value: '/example-segments.jsonc', label: 'Multi-Segment (Mixed Strategy)' },
];

// Search params schema
const customChartSearchSchema = z.object({
  data: z.string().optional(),
});

// LocalStorage key for tracking onboarding
const ONBOARDING_STORAGE_KEY = 'custom-chart-onboarding-seen';

export const Route = createFileRoute('/custom-chart')({
  validateSearch: customChartSearchSchema,
  component: CustomChart,
});

// ============================================================================
// GettingStartedModal
// ============================================================================

interface GettingStartedModalProps {
  opened: boolean;
  onClose: () => void;
}

function GettingStartedModal({ opened, onClose }: GettingStartedModalProps) {
  return (
    <Modal opened={opened} onClose={onClose} title="Custom Charts" size="md" centered>
      <Stack gap="md">
        <Text>Create your own poker hand visualizations by providing chart data in JSON format.</Text>

        <Text fw={500}>How it works:</Text>
        <List size="sm" spacing="xs">
          <List.Item>Click the edit button to open the JSON editor</List.Item>
          <List.Item>Load a template or write your own chart data</List.Item>
          <List.Item>Apply changes to see your visualization</List.Item>
          <List.Item>Share your chart via the URL or download as an image</List.Item>
        </List>

        <Text size="sm" c="dimmed">
          Check out the example templates for reference on the data format.
        </Text>

        <Button onClick={onClose} fullWidth>
          Get Started
        </Button>
      </Stack>
    </Modal>
  );
}

// ============================================================================
// HelpButton (for navbar)
// ============================================================================

const TOOLTIP_STYLES = {
  tooltip: {
    backgroundColor: 'var(--mantine-color-dark-7)',
    border: '1px solid var(--mantine-color-dark-4)',
    color: 'var(--mantine-color-white)',
  },
};

function HelpButton({ onClick }: { onClick: () => void }) {
  return (
    <Tooltip label="Help" position="left" styles={TOOLTIP_STYLES}>
      <ActionIcon variant="subtle" size="lg" onClick={onClick} aria-label="Help">
        <IconHelp size={20} />
      </ActionIcon>
    </Tooltip>
  );
}

// ============================================================================
// EditChartModal
// ============================================================================

interface EditChartModalProps {
  opened: boolean;
  onClose: () => void;
  jsonText: string;
  onJsonTextChange: (text: string) => void;
  validationErrors: string[];
  onApply: () => void;
  isApplying: boolean;
}

function EditChartModal({
  opened,
  onClose,
  jsonText,
  onJsonTextChange,
  validationErrors,
  onApply,
  isApplying,
}: EditChartModalProps) {
  const isMobile = useIsMobile();
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(EXAMPLE_TEMPLATES[0].value);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  const handleLoadTemplate = useCallback(async () => {
    if (!selectedTemplate) return;

    setIsLoadingTemplate(true);
    try {
      const response = await fetch(selectedTemplate);
      if (!response.ok) {
        throw new Error('Failed to fetch template');
      }
      const text = await response.text();
      onJsonTextChange(text);
      notifications.show({
        title: 'Template loaded',
        message: 'Click "Apply Changes" to update the chart',
        color: 'blue',
      });
    } catch {
      notifications.show({
        title: 'Load failed',
        message: 'Failed to load template. Please try again.',
        color: 'red',
      });
    } finally {
      setIsLoadingTemplate(false);
    }
  }, [selectedTemplate, onJsonTextChange]);

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch('/example-chart.jsonc');
      if (!response.ok) {
        throw new Error('Failed to fetch template');
      }
      const text = await response.text();

      const blob = new Blob([text], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'chart-template.jsonc';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      notifications.show({
        title: 'Download failed',
        message: 'Failed to download template. Please try again.',
        color: 'red',
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group justify="space-between" style={{ width: '100%', paddingRight: 16 }}>
          <Text fw={500} size="lg">
            Edit Chart Data
          </Text>
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconDownload size={16} />}
            onClick={handleDownloadTemplate}
          >
            Download template
          </Button>
        </Group>
      }
      size="90%"
      fullScreen={isMobile}
      styles={{
        body: {
          height: isMobile ? 'calc(100dvh - 70px)' : '70dvh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <Stack gap="md" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Group gap="sm">
          <Select
            data={EXAMPLE_TEMPLATES}
            value={selectedTemplate}
            onChange={setSelectedTemplate}
            placeholder="Select a template"
            size="sm"
            style={{ flex: 1 }}
          />
          <Button variant="light" size="sm" onClick={handleLoadTemplate} loading={isLoadingTemplate}>
            Load
          </Button>
        </Group>

        {validationErrors.length > 0 && (
          <Alert icon={<IconAlertCircle size={16} />} title="Validation Errors" color="red">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validationErrors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <CodeMirror
            value={jsonText}
            extensions={[jsonc()]}
            onChange={onJsonTextChange}
            theme="dark"
            basicSetup={{
              lineNumbers: true,
              highlightActiveLineGutter: true,
              highlightActiveLine: true,
              foldGutter: true,
            }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onApply} loading={isApplying}>
            Apply Changes
          </Button>
        </div>
      </Stack>
    </Modal>
  );
}

// ============================================================================
// CustomChart (main component)
// ============================================================================

function CustomChart() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: dataParam } = Route.useSearch();

  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [jsonText, setJsonText] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  // Track if user has seen onboarding
  const [hasSeenOnboarding, setHasSeenOnboarding] = useLocalStorage({
    key: ONBOARDING_STORAGE_KEY,
    defaultValue: false,
  });
  const [helpModalOpened, { open: openHelpModal, close: closeHelpModal }] = useDisclosure(false);

  // Show onboarding on first visit
  const showOnboarding = !hasSeenOnboarding && chartData !== null;

  const handleCloseOnboarding = useCallback(() => {
    setHasSeenOnboarding(true);
    closeHelpModal();
  }, [setHasSeenOnboarding, closeHelpModal]);

  // Set chart name for navbar and document title and apply defaults
  useChartTitle(chartData);
  useChartDefaults(chartData?.defaults);

  usePageActions({
    secondary: () => (
      <>
        <RangeFilterButton />
        <CopyLinkButton />
        <DownloadButton />
      </>
    ),
    primary: () => <ConfigureChartButton />,
  });

  // Validate and update chart (updates URL with encoded data)
  const handleApply = useCallback(() => {
    setIsApplying(true);
    try {
      const parsed = parseJsonc(jsonText);
      const result = validateChartData(parsed);

      if (result.success && result.data) {
        setValidationErrors([]);
        setChartData(result.data);

        // Encode and update URL
        const encoded = encodeChartData(result.data);
        navigate({ search: { data: encoded }, replace: true });

        notifications.show({
          title: 'Chart updated',
          message: 'Your changes have been applied successfully',
          color: 'green',
        });

        closeEditModal();
      } else {
        setValidationErrors(result.errors || ['Unknown validation error']);
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        setValidationErrors([`JSON Parse Error: ${error.message}`]);
      } else if (error instanceof Error) {
        setValidationErrors([`Parse Error: ${error.message}`]);
      } else {
        setValidationErrors(['Failed to parse JSON']);
      }
    } finally {
      setIsApplying(false);
    }
  }, [jsonText, closeEditModal, navigate]);

  // Load example JSON using React Query (only if no URL param)
  const {
    data: exampleJson,
    error: fetchError,
    isLoading,
  } = useQuery({
    queryKey: ['example-chart'],
    queryFn: async () => {
      const response = await fetch('/example-chart.jsonc');
      if (!response.ok) {
        throw new Error('Failed to fetch example chart');
      }
      return response.text();
    },
    staleTime: Infinity,
    enabled: !dataParam,
  });

  // Initialize chart data from URL param or example
  useEffect(() => {
    if (dataParam) {
      try {
        const decoded = decodeChartData(dataParam);
        if (decoded) {
          setChartData(decoded);
          setJsonText(JSON.stringify(decoded, null, 2));
        } else {
          setValidationErrors(['Failed to decode chart data from URL']);
        }
      } catch {
        setValidationErrors(['Failed to decode chart data from URL. The data may be corrupted or invalid.']);
      }
    } else if (exampleJson) {
      try {
        const parsed = parseJsonc(exampleJson);
        const result = validateChartData(parsed);
        if (result.success && result.data) {
          setChartData(result.data);
          setJsonText(exampleJson);

          const encoded = encodeChartData(result.data);
          navigate({ search: { data: encoded }, replace: true });
        }
      } catch {
        // Ignore initial parse errors
      }
    }
  }, [dataParam, exampleJson, navigate]);

  // Show fetch error if any
  useEffect(() => {
    if (fetchError) {
      setValidationErrors([`Failed to load example chart: ${fetchError.message}`]);
    }
  }, [fetchError]);

  // Clear validation errors when json text changes (user is editing)
  const handleJsonTextChange = useCallback(
    (text: string) => {
      setJsonText(text);
      if (validationErrors.length > 0) {
        setValidationErrors([]);
      }
    },
    [validationErrors.length],
  );

  return (
    <>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        {isLoading ? (
          <Center style={{ width: '100%', height: '100%' }}>
            <Loader size="xl" />
          </Center>
        ) : chartData ? (
          <Chart3D
            data={chartData}
            controlPaneContent={
              <>
                <HelpButton onClick={openHelpModal} />
                <Tooltip label="Edit data" position="left" styles={TOOLTIP_STYLES}>
                  <ActionIcon
                    variant="gradient"
                    gradient={{ from: 'violet', to: 'blue', deg: 135 }}
                    size="lg"
                    radius="md"
                    onClick={openEditModal}
                    aria-label="Edit data"
                  >
                    <IconEdit size={20} />
                  </ActionIcon>
                </Tooltip>
              </>
            }
          />
        ) : null}
      </div>

      <GettingStartedModal opened={showOnboarding || helpModalOpened} onClose={handleCloseOnboarding} />

      <EditChartModal
        opened={editModalOpened}
        onClose={closeEditModal}
        jsonText={jsonText}
        onJsonTextChange={handleJsonTextChange}
        validationErrors={validationErrors}
        onApply={handleApply}
        isApplying={isApplying}
      />
    </>
  );
}
